import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  useMyMonitoringDeleteSystemUser,
  useMyMonitoringGetSystemUser,
  useMyMonitoringUpdateSystemUser
} from '@apps-common/apis/my-monitoring/users'
import { useDeleteSiteNamedUser, useGetUnitNamedUser, useUpdateSiteNamedUser } from '@apps-common/apis/portal/users'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ConfirmModalAction } from '@apps-common/components/modals'
import { ModalBody, ModalFooter, ModalTopConfirmActionButton } from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { ISiteNamedUserModel } from '@apps-common/types/portal/users'

interface IProps {
  userId: number
  unitId: string
  closeCb: (role: string) => void
}

export const SystemUserUpdateForm = ({ userId, unitId, closeCb }: IProps) => {
  const useGetSystemUserHook = useProjectHook(useGetUnitNamedUser, useMyMonitoringGetSystemUser)
  const useUpdateSystemUserHook = useProjectHook(useUpdateSiteNamedUser, useMyMonitoringUpdateSystemUser)
  const useDeleteSystemUserHook = useProjectHook(useDeleteSiteNamedUser, useMyMonitoringDeleteSystemUser)

  const { data: user, isLoading: userIsLoading, isError: userIsError } = useGetSystemUserHook({ config: {}, userId, unitId })

  const { mutate: updateUser, isPending: updateIsPending } = useUpdateSystemUserHook(true, () => closeCb(ModalDismiss.Confirm))

  const { mutate: deleteUser, isPending: deleteIsPending } = useDeleteSystemUserHook(true, () => closeCb(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset
  } = useForm<ISiteNamedUserModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      unitNamedUserId: userId,
      unitId,
      userName: '',
      editDate: new Date().toISOString()
    }
  })

  useEffect(() => {
    if (user) {
      reset(user)
    }
  }, [user, reset])

  const submit = (values: ISiteNamedUserModel) => {
    if (values.partition && values.partition.length > 0) {
      values.mapPartition = true
    }

    updateUser({ model: values })
  }

  const isLoading = userIsLoading || userIsError
  const isUpdating = isSubmitting || updateIsPending || deleteIsPending

  return (
    <>
      <ModalBody classes='!pt-[95px]'>
        <div className='flex items-center justify-end'>
          <ModalTopConfirmActionButton
            loading={isLoading}
            confirmProps={{
              text: 'Remove user?',
              description: 'Are you sure you would like to remove user from this site?',
              action: ConfirmModalAction.Error
            }}
            confirmCallback={() => deleteUser({ unitId, userId })}
            text='Delete'
            icon='delete'
            classes='action-error'
          />
        </div>
        <form noValidate id='system-user-update-form' onSubmit={handleSubmit(submit)}>
          <VertSpacerMd />
          <div className='grid sm:grid-cols-2 gap-[10px]'>
            <InputSM
              key='firstName'
              type='text'
              placeholder='First name'
              label='First name'
              id='firstName'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('firstName', {
                required: true,
                maxLength: {
                  message: 'No longer than 15 characters',
                  value: 15
                }
              })}
              errors={errors.firstName}
            />
            <InputSM
              key='lastName'
              type='text'
              placeholder='Last name'
              label='Last name'
              id='lastName'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('lastName', {
                required: true,
                maxLength: {
                  message: 'No longer than 15 characters',
                  value: 15
                }
              })}
              errors={errors.lastName}
            />
          </div>
          <InputSM
            key='userNumber'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='userNumber'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('zone', {
              required: true,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.zone}
          />
          <InputSM
            key='userPartition'
            type='text'
            placeholder='0 to 99'
            label='User partition (If required)'
            id='userPartition'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('partition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.partition}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between gap-2'>
          <ButtonRoundedSm
            type='submit'
            form='system-user-update-form'
            loading={isLoading || isUpdating}
            text='Update'
            className='action-success'
          />
          <ButtonRoundedSm
            type='button'
            loading={isLoading || isUpdating}
            text='Cancel'
            className='action-neutral'
            callback={() => closeCb(ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </>
  )
}
